import * as React from 'react'
import { PushPin, PencilSimple, ArrowRight, EyeSlash } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import {
  Card,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
} from '../core/card'
import { Popover, PopoverTrigger, PopoverContent } from '../core/popover'
import { HelpPopover } from '../core/help-popover'
import { Skeleton } from '../core/skeleton'
import { EntityGlyph, type EntityRef, entityTypeConfig } from '../core/entity-glyph'
import { softGradient } from '../core/badge-colors'
import { type BadgeColor } from '../core/badge'
import { WidgetMenu, type WidgetMenuItem } from './widget-menu'

/**
 * PinnedWidget — the homepage "Pinned" card: a horizontal strip of the records a
 * user has pinned for quick access (contacts, companies, deals, campaigns, …),
 * each a tile with the record name, its {@link EntityGlyph}, and its type label.
 *
 *   <PinnedWidget items={pins} onEdit={openEditPins} onHide={hidePinned} />
 *
 * A tile links to its record's page (`href`), or — for records with no page yet
 * (a contact, note, document) — reveals an {@link EntityPreviewCard} in a popover
 * when the item carries a `preview`. The header ⋯ menu offers "Edit pins" (wire
 * to the `EditPinsModal` block) and, when `onHide` is set, "Hide widget".
 * Scrolls horizontally when the pins overflow.
 */

/** A pinned record — the shared {@link EntityRef} shape. */
export type PinnedItem = EntityRef

export interface PinnedWidgetProps {
  items: PinnedItem[]
  /** Called when a tile with neither `href` nor `preview` is clicked. */
  onItemClick?: (item: PinnedItem) => void
  /** Wire the ⋯ menu's "Edit pins" action (opens the Edit Pins modal); hidden when omitted. */
  onEdit?: () => void
  /** Wire the ⋯ menu's "View all" action (navigates to the full pins list); hidden when omitted. */
  onViewAll?: () => void
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  /** Density — `md` (default) or `sm` (smaller tiles, for a tight dashboard column). */
  size?: 'sm' | 'md'
  /** Card heading. */
  title?: React.ReactNode
  /** Help blurb shown in a `?` popover beside the title (what can be pinned). */
  help?: React.ReactNode
  /** Text shown when there are no pins. */
  emptyLabel?: string
  /**
   * Fill the card's height and stretch the pin tiles to match, instead of the
   * default fixed-height row. Use when the card sits in a stretched cell (a
   * half-width homepage column sized to a taller neighbor) so it doesn't leave
   * dead space below a single tile row.
   */
  fillHeight?: boolean
  /** Show a loading skeleton (tile placeholders) instead of items. */
  loading?: boolean
  className?: string
}

type PinnedSize = 'sm' | 'md'

const tileClass = (size: PinnedSize, color: BadgeColor, fillHeight?: boolean) =>
  cn(
    // Faint type-colored ombre from the glyph corner (top-right) fading out by
    // ~55% over the white tile — the "current homepage" pin treatment. Subtlety
    // comes from the short gradient reach (`to-55%`), not opacity: the `fbui-*`
    // colors don't support the `/opacity` modifier (it drops the gradient).
    'flex shrink-0 flex-col justify-between rounded-lg border border-fbui-gray-200 bg-fbui-base-white bg-gradient-to-bl to-transparent to-55% text-left outline-none transition-all',
    softGradient[color],
    'hover:border-fbui-gray-300 hover:shadow-fbui-sm focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
    size === 'sm' ? 'w-40 p-2.5' : 'w-48 p-[0.75rem]',
    // Fixed height in isolation. When `fillHeight` is set, the tiles stretch to
    // fill a card that's taller than one tile row (a half-width homepage cell
    // stretched to match a neighbor), keeping a floor at the natural height so
    // `justify-between` still pins the name to the top and the type to the base.
    fillHeight
      ? size === 'sm'
        ? 'h-full min-h-[4rem]'
        : 'h-full min-h-[5rem]'
      : size === 'sm'
        ? 'h-16'
        : 'h-20'
  )

/** One pinned record: name + type label over a corner {@link EntityGlyph}. */
const PinnedTile = ({
  item,
  size,
  onItemClick,
  fillHeight,
}: {
  item: PinnedItem
  size: PinnedSize
  onItemClick?: (item: PinnedItem) => void
  fillHeight?: boolean
}) => {
  const [previewOpen, setPreviewOpen] = React.useState(false)

  // Dismiss the preview on scroll. The popover is anchored to a tile that lives
  // in scrollable regions (the horizontal pin strip + the page), so following
  // the anchor through a scroll drags the card to bad positions (off-screen,
  // over the nav). Closing on scroll is the tooltip-standard behavior — but only
  // for scrolls OUTSIDE the popover; a scroll that originates inside the card
  // (e.g. a long note's scrollable body) must not dismiss it. Attach next frame
  // so the open itself (focus/layout) doesn't self-dismiss.
  React.useEffect(() => {
    if (!previewOpen) return
    const onScroll = (e: Event) => {
      const t = e.target
      if (
        t instanceof Element &&
        t.closest('[data-radix-popper-content-wrapper]')
      ) {
        return
      }
      setPreviewOpen(false)
    }
    const raf = requestAnimationFrame(() =>
      window.addEventListener('scroll', onScroll, {
        capture: true,
        passive: true,
      })
    )
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [previewOpen])

  const cfg = entityTypeConfig[item.type]
  const label = item.typeLabel ?? cfg.label
  const ariaLabel = `${item.name} — ${label}`
  const cls = tileClass(size, cfg.color, fillHeight)
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span
          className={cn(
            'truncate font-semibold text-fbui-gray-900',
            size === 'sm' ? 'text-xs' : 'text-sm'
          )}
        >
          {item.name}
        </span>
        <EntityGlyph type={item.type} size="xs" />
      </div>
      <span className="truncate text-xs font-medium uppercase text-fbui-gray-400">
        {label}
      </span>
    </>
  )

  // Preview record (no page yet) — reveal the preview card in a popover. Kept
  // controlled so a render-function preview can dismiss it (its close ×).
  if (item.preview != null) {
    return (
      <Popover open={previewOpen} onOpenChange={setPreviewOpen}>
        <PopoverTrigger asChild>
          <button type="button" aria-label={ariaLabel} className={cls}>
            {body}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          // Behave like a tooltip: keep 16px off every viewport edge and let
          // Radix flip/shift so a tile near a page edge (e.g. the last pin, or
          // a scrolled strip) opens toward the available space instead of
          // spilling off-screen. `avoidCollisions` is on by Radix default.
          collisionPadding={16}
          className="w-auto border-none bg-transparent p-0 shadow-none"
        >
          {typeof item.preview === 'function'
            ? item.preview(() => setPreviewOpen(false))
            : item.preview}
        </PopoverContent>
      </Popover>
    )
  }
  // Record with its own page.
  if (item.href != null) {
    return (
      <a href={item.href} aria-label={ariaLabel} className={cls}>
        {body}
      </a>
    )
  }
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => onItemClick?.(item)}
      className={cls}
    >
      {body}
    </button>
  )
}

export const PinnedWidget = React.forwardRef<HTMLDivElement, PinnedWidgetProps>(
  (
    { items, onItemClick, onEdit, onViewAll, onHide, size = 'md', title = 'Pinned', help, emptyLabel = 'No pins yet', fillHeight, loading, className },
    ref
  ) => {
    if (loading) {
      return (
        <Card
          ref={ref}
          size="sm"
          className={cn(
            'w-full max-w-3xl',
            fillHeight && 'flex h-full flex-col',
            className
          )}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <PushPin
                aria-hidden="true"
                className="size-4 text-fbui-gray-500"
              />
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent className={cn(fillHeight && 'flex-1')}>
            <div className="flex gap-[0.75rem] overflow-hidden pb-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className={cn(
                    'shrink-0 rounded-lg',
                    size === 'sm' ? 'h-16 w-40' : 'h-20 w-48'
                  )}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )
    }
    const menuItems: WidgetMenuItem[] = [
      ...(onEdit
        ? [{ key: 'edit', label: 'Edit pins', icon: <PencilSimple />, onSelect: onEdit }]
        : []),
      ...(onViewAll
        ? [{ key: 'view-all', label: 'View all', icon: <ArrowRight />, onSelect: onViewAll }]
        : []),
      ...(onHide
        ? [{ key: 'hide', label: 'Hide widget', icon: <EyeSlash />, onSelect: onHide }]
        : []),
    ]
    return (
      <Card
        ref={ref}
        size="sm"
        className={cn(
          'w-full max-w-3xl',
          // Fill a stretched cell so the tiles (below) can grow into it.
          fillHeight && 'flex h-full flex-col',
          className
        )}
      >
        {/* Tighter title→content gap than the Card default (p-4 bottom). */}
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <PushPin aria-hidden="true" className="size-4 text-fbui-gray-500" />
            {title}
            {help != null && (
              // `md` panel (14px) — the pin help is a paragraph, not a
              // one-line table-header blurb, so the default `sm`/12px reads too
              // small.
              <HelpPopover triggerSize="sm" size="md">
                {help}
              </HelpPopover>
            )}
          </CardTitle>
          {menuItems.length > 0 && (
            <CardAction>
              <WidgetMenu items={menuItems} label="Pinned options" />
            </CardAction>
          )}
        </CardHeader>
        <CardContent className={cn(fillHeight && 'flex-1')}>
          {items.length > 0 ? (
            <div
              className={cn(
                'fbui-scroll flex gap-[0.75rem] overflow-x-auto pb-1',
                // Give the tile row the full content height so `h-full` tiles fill it.
                fillHeight && 'h-full'
              )}
            >
              {items.map((item) => (
                <PinnedTile
                  key={item.id}
                  item={item}
                  size={size}
                  onItemClick={onItemClick}
                  fillHeight={fillHeight}
                />
              ))}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-fbui-gray-500">{emptyLabel}</p>
          )}
        </CardContent>
      </Card>
    )
  }
)
PinnedWidget.displayName = 'PinnedWidget'
